import { useState } from 'react';

import type { ProductCompIn, ProductOut, TerminalOut } from '@solar/api-client';
import { ApiError } from '@solar/api-client';
import { ErrorBox, Spinner, useAsync, won } from '@solar/ui';

import { api } from '../api';
import { Modal } from '../Modal';

/**
 * 상품 관리 — 레거시 define/product 파리티.
 *
 * 구성 단말 편집은 배열 전체 동기화다: 저장 시 목록에 없는 기존 구성품은
 * 서버가 삭제한다(레거시 del_arr 파리티). 단말 중복은 서버가 422 로 거부한다.
 */

const SIZE = 30;

/** 구성품 편집 행 — 저장 시 ProductCompIn 으로 변환한다. */
interface CompRow {
  id?: number;
  terminalId: string;
  qty: string;
}

export function ProductManage() {
  const [keyword, setKeyword] = useState('');
  const [applied, setApplied] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const terminals = useAsync(() => api.listTerminals({ size: 200 }), []);
  const list = useAsync(
    () => api.listProducts({ keyword: applied, page, size: SIZE }),
    [applied, page],
  );

  const total = list.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / SIZE));
  const row = list.data?.items.find((p) => p.id === selected) ?? null;

  return (
    <div className="admin__body">
      <section className="admin__list">
        <div className="toolbar">
          <form
            className="search"
            onSubmit={(e) => {
              e.preventDefault();
              setApplied(keyword.trim());
              setPage(1);
            }}
          >
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="상품 명칭"
            />
            <button type="submit">검색</button>
          </form>
          <button type="button" className="primary create" onClick={() => setCreateOpen(true)}>
            등록
          </button>
        </div>

        {list.loading && <Spinner />}
        {list.error && <ErrorBox message={list.error} onRetry={list.reload} />}

        {list.data && (
          <table className="grid">
            <thead>
              <tr>
                <th>상품 명칭</th>
                <th className="num">금액</th>
                <th className="num">단말 개수</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.length === 0 && (
                <tr>
                  <td colSpan={3} className="muted">
                    등록된 상품이 없습니다.
                  </td>
                </tr>
              )}
              {list.data.items.map((p) => (
                <tr
                  key={p.id}
                  className={p.id === selected ? 'selected' : ''}
                  onClick={() => setSelected(p.id)}
                >
                  <td>{p.name}</td>
                  <td className="num">{won(p.price)}</td>
                  <td className="num">
                    {p.components.reduce((sum, c) => sum + (c.qty ?? 0), 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="pager">
          <button type="button" onClick={() => setPage((p) => p - 1)} disabled={page <= 1}>
            이전
          </button>
          <span>
            {page} / {lastPage} (총 {total}건)
          </span>
          <button type="button" onClick={() => setPage((p) => p + 1)} disabled={page >= lastPage}>
            다음
          </button>
        </div>
      </section>

      <aside className="admin__aside">
        {row === null ? (
          <p className="muted">상품을 선택하세요.</p>
        ) : (
          <ProductEdit
            key={row.id}
            row={row}
            terminals={terminals.data?.items ?? []}
            onClose={() => setSelected(null)}
            onChanged={() => list.reload()}
            onDeleted={() => {
              setSelected(null);
              list.reload();
            }}
          />
        )}
      </aside>

      {createOpen && (
        <ProductCreateModal
          terminals={terminals.data?.items ?? []}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            list.reload();
          }}
        />
      )}
    </div>
  );
}

function toInt(value: string): number | undefined {
  const n = Number(value);
  return value.trim() !== '' && Number.isFinite(n) ? Math.trunc(n) : undefined;
}

/** 편집 행 → 서버 본문. 미완성 행(단말 미선택)이 있으면 null. */
function toComponents(rows: CompRow[]): ProductCompIn[] | null {
  const out: ProductCompIn[] = [];
  for (const r of rows) {
    const terminalId = toInt(r.terminalId);
    const qty = toInt(r.qty);
    if (terminalId === undefined || qty === undefined || qty < 1) return null;
    out.push(r.id === undefined ? { def_terminal_id: terminalId, qty } : { id: r.id, def_terminal_id: terminalId, qty });
  }
  return out;
}

function CompEditor({
  rows,
  terminals,
  onChange,
}: {
  rows: CompRow[];
  terminals: TerminalOut[];
  onChange: (rows: CompRow[]) => void;
}) {
  function update(index: number, patch: Partial<CompRow>) {
    onChange(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  return (
    <div className="comp-editor">
      {rows.map((r, i) => (
        // 신규 행은 고유 id 가 없어 배열 위치로 구분한다(행 순서 변경 UI 없음).
        // eslint-disable-next-line react/no-array-index-key
        <div className="row comp-row" key={r.id ?? `new-${i}`}>
          <select
            value={r.terminalId}
            onChange={(e) => update(i, { terminalId: e.target.value })}
          >
            <option value="">단말 선택</option>
            {terminals.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.model ? ` (${t.model})` : ''}
              </option>
            ))}
          </select>
          <input
            type="number"
            min={1}
            className="qty"
            value={r.qty}
            onChange={(e) => update(i, { qty: e.target.value })}
            placeholder="갯수"
          />
          <button type="button" onClick={() => onChange(rows.filter((_, j) => j !== i))}>
            삭제
          </button>
        </div>
      ))}
      <div className="row">
        <button type="button" onClick={() => onChange([...rows, { terminalId: '', qty: '1' }])}>
          구성 단말 추가
        </button>
      </div>
    </div>
  );
}

function ProductEdit({
  row,
  terminals,
  onClose,
  onChanged,
  onDeleted,
}: {
  row: ProductOut;
  terminals: TerminalOut[];
  onClose: () => void;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const [name, setName] = useState(row.name);
  const [price, setPrice] = useState(row.price === null ? '' : String(row.price));
  const [comps, setComps] = useState<CompRow[]>(
    row.components.map((c) => ({
      id: c.id,
      terminalId: c.def_terminal_id === null ? '' : String(c.def_terminal_id),
      qty: c.qty === null ? '1' : String(c.qty),
    })),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function save() {
    const components = toComponents(comps);
    if (!name.trim()) {
      setError('상품 명칭을 입력해 주세요.');
      return;
    }
    if (components === null || components.length === 0) {
      setError('구성 단말을 1개 이상, 단말과 갯수를 모두 채워 주세요.');
      return;
    }
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      const saved = await api.updateProduct(row.id, {
        name: name.trim(),
        price: toInt(price),
        components,
      });
      // 서버가 확정한 구성품 id 로 편집 상태를 다시 맞춘다(신규 행 id 부여).
      setComps(
        saved.components.map((c) => ({
          id: c.id,
          terminalId: c.def_terminal_id === null ? '' : String(c.def_terminal_id),
          qty: c.qty === null ? '1' : String(c.qty),
        })),
      );
      setOk('저장했습니다.');
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await api.deleteProduct(row.id);
      onDeleted();
    } catch (err) {
      // "해당 상품을 사용하고 있는 주문이 있습니다." 등 409 메시지 그대로.
      setError(err instanceof ApiError ? err.message : '삭제하지 못했습니다.');
      setConfirmDelete(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="detail">
      <div className="detail__head">
        <h2>{row.name}</h2>
        <button type="button" className="link" onClick={onClose}>
          닫기
        </button>
      </div>

      <section className="panel form-grid">
        <label className="f wide">
          상품 명칭 *
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="f wide">
          금액
          <input type="number" min={0} value={price} onChange={(e) => setPrice(e.target.value)} />
        </label>
      </section>

      <h3 className="section">구성 단말</h3>
      <section className="panel">
        <CompEditor rows={comps} terminals={terminals} onChange={setComps} />
        <p className="muted">
          저장하면 목록 그대로 반영됩니다 — 여기서 뺀 구성 단말은 삭제됩니다.
        </p>
      </section>

      {error && <p className="error">{error}</p>}
      {ok && <p className="ok">{ok}</p>}

      <div className="row form-actions">
        <button type="button" disabled={busy} onClick={() => setConfirmDelete(true)}>
          삭제
        </button>
        <button type="button" className="primary" disabled={busy} onClick={() => void save()}>
          {busy ? '저장 중…' : '저장'}
        </button>
      </div>

      {confirmDelete && (
        <Modal title="상품 삭제" onClose={() => setConfirmDelete(false)}>
          <p>
            <strong>{row.name}</strong> 상품을 삭제합니다. 계속할까요?
          </p>
          <div className="row">
            <button type="button" onClick={() => setConfirmDelete(false)}>
              취소
            </button>
            <button type="button" className="primary" disabled={busy} onClick={() => void remove()}>
              삭제
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function ProductCreateModal({
  terminals,
  onClose,
  onCreated,
}: {
  terminals: TerminalOut[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [comps, setComps] = useState<CompRow[]>([{ terminalId: '', qty: '1' }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    const components = toComponents(comps);
    if (!name.trim()) {
      setError('상품 명칭을 입력해 주세요.');
      return;
    }
    if (components === null || components.length === 0) {
      setError('구성 단말을 1개 이상, 단말과 갯수를 모두 채워 주세요.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.createProduct({ name: name.trim(), price: toInt(price), components });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '등록하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="상품 등록" onClose={onClose}>
      <label className="labeled">
        상품 명칭 *
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="labeled">
        금액
        <input type="number" min={0} value={price} onChange={(e) => setPrice(e.target.value)} />
      </label>
      <CompEditor rows={comps} terminals={terminals} onChange={setComps} />

      {error && <p className="error">{error}</p>}
      <div className="row">
        <button type="button" onClick={onClose}>
          취소
        </button>
        <button type="button" className="primary" disabled={busy} onClick={() => void submit()}>
          {busy ? '등록 중…' : '등록'}
        </button>
      </div>
    </Modal>
  );
}
